import { useEffect, useRef, useState } from 'react'
import { Image } from '@/atoms/Image'

export default function BattlefieldImage() {
  const [translateY, setTranslateY] = useState(0)
  const sectionRef = useRef<HTMLElement | null>(null)
  const prevTopRef = useRef<number | null>(null)
  const prevRatioRef = useRef<number | null>(null)

  useEffect(() => {
    const element = sectionRef.current
    if (!element || typeof window === 'undefined') return

    const thresholds: number[] = []
    for (let i = 0; i <= 100; i += 1) thresholds.push(i / 100)

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry) return
        const ratio = entry.intersectionRatio // 0..1

        const currentTop = entry.boundingClientRect.top
        const previousTop = prevTopRef.current
        const previousRatio = prevRatioRef.current

        // Determine scroll direction based on element movement relative to viewport
        // If the element's top decreases, user is scrolling down; we move image up (negative Y)
        let directionSign = 0
        if (previousTop !== null) {
          directionSign = currentTop < previousTop ? -1 : 1
        }

        // Scale movement by magnitude of intersection change to keep it gentle
        const deltaRatio = Math.abs(ratio - (previousRatio ?? ratio))
        const movementPercent = deltaRatio * 7 // full 0..1 sweep => 20% total move

        if (directionSign !== 0 && movementPercent > 0) {
          setTranslateY((prev) => {
            const next = prev + directionSign * movementPercent
            return Math.max(-10, Math.min(10, next))
          })
        }

        prevTopRef.current = currentTop
        prevRatioRef.current = ratio
      },
      { root: null, rootMargin: '0px', threshold: thresholds },
    )

    observer.observe(element)

    return () => {
      observer.unobserve(element)
      observer.disconnect()
    }
  }, [])

  return (
    <section ref={sectionRef} className="relative h-96 overflow-hidden md:h-[90vh]">
      <div
        className="absolute inset-0 -top-[10%] h-[120%] w-full md:h-[120%]"
        style={{ transform: `translateY(${translateY}%)` }}
      >
        <Image
          src="v1759419820/theatrical/young%20washington/yw-trailer-img-screen-05"
          alt="Young Washington background scene"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
          fill
        />
      </div>
    </section>
  )
}
